import { useToast } from "@mallardbay/lib-react-components"

import { useUpdateOutfitterMutation } from "~graphql/outfitters.generated"
import type { UpdateOutfitterInput } from "~graphql/generated/types"
import COPY_CONSTANTS from "~config/copy-constants"
import { useTrackEditOutfitter } from "~utils/event-tracking/event-tracking"

export function useUpdateOutfitter({
    outfitterId,
    onCompleted,
    updatedSection,
    shouldShowSuccessToast = true,
}: {
    outfitterId: string
    onCompleted?: () => void
    updatedSection: string
    shouldShowSuccessToast?: boolean
}) {
    const { showSuccess, showError } = useToast()
    const successMessage = COPY_CONSTANTS.OUTFITTER_UPDATED_SUCCESSFULLY

    const trackEditOutfitter = useTrackEditOutfitter()

    // NOTE: we intentionally do NOT refetch `portalEntities` / `outfitter` here.
    // The mutation returns the full `OutfitterFields` fragment, which Apollo
    // normalizes by id, so both queries update automatically. Refetching them
    // briefly flips their loading state, which cascades to `SelfOnboardRedirect`
    // rendering its loader and unmounting open modals/drawers mid-interaction.
    const [updateOutfitterMutation, { loading }] = useUpdateOutfitterMutation({
        onError: showError,
    })

    const updateOutfitter = ({
        data,
        aiSuggestionId,
        onSuccess,
    }: {
        data: UpdateOutfitterInput
        aiSuggestionId?: string
        onSuccess?: () => void
    }) => {
        return updateOutfitterMutation({
            variables: { id: outfitterId, data: data, aiSuggestionId },
            onCompleted: () => {
                if (shouldShowSuccessToast) showSuccess(successMessage)
                onCompleted?.()
                onSuccess?.()
                trackEditOutfitter({
                    outfitterId,
                    updatedSection,
                })
            },
        })
    }

    return { updateOutfitter, isLoading: loading }
}
